import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { handleEmailAction } from '../utils/emailActionHandler';
import { auth } from '../Services/Firebase/FirebaseIndex';
import GigifyLogo from '../assets/Logo/GIGiFY Logo Horizontal no tagline.png';
import BandCrowdBg from '../assets/Images/bandcrowd2.jpg';
import debug from '../utils/debug';

const EmailVerified = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(10);

  useEffect(() => {
    const processVerification = async () => {
      try {
        const mode = searchParams.get('mode');
        const actionCode = searchParams.get('oobCode');

        debug.log('EmailVerified', 'Email verification page loaded', { 
          mode, 
          hasActionCode: !!actionCode 
        });

        // Only process if we have valid parameters
        if (mode === 'verifyEmail' && actionCode) {
          // Try to process the verification silently
          await handleEmailAction(actionCode, mode);
          
          // Force reload the current user if they're logged in
          if (auth.currentUser) {
            await auth.currentUser.reload();
            debug.log('EmailVerified', 'User reloaded', {
              emailVerified: auth.currentUser.emailVerified
            });
          }
        }
      } catch (error) {
        // Silently log errors but don't change the UI
        debug.log('EmailVerified', 'Silent processing completed', error);
      }
    };

    processVerification();
  }, [searchParams]);

  // Auto-close countdown
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);

      return () => clearTimeout(timer);
    } else if (countdown === 0) {
      window.close();
    }
  }, [countdown]);

  const handleCloseNow = () => {
    window.close();
  };

  const handleLoginRedirect = () => {
    // If user is logged in, get their UID for redirect
    if (auth.currentUser) {
      // Try to determine user type from session storage or default to talent
      const userType = sessionStorage.getItem('gigify_user_type') || 'talent';
      navigate(`/${userType}-blueprint/${auth.currentUser.uid}`, { replace: true });
    } else {
      navigate('/login', { replace: true });
    }
  };

  // Color Palette (matching EmailVerification)
  const colors = {
    darkBlue: "#002244",
    mediumBlue: "#144874",
    brightBlue: "#01a2f1",
    orange: "#f85008",
    orangeHover: "#e6470a",
    success: "#00d084",
    successLight: "rgba(0, 208, 132, 0.1)",
    error: "#ff4444",
    errorLight: "rgba(255, 68, 68, 0.1)",
    warning: "#ff9800",
    white: "#fff",
    lightGray: "#f8f9fa",
    mediumGray: "#666",
    darkGray: "#999",
    borderGray: "#e0e0e0",
  };


  return (
    <>
      <style>{`
        .email-verified-container {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(135deg, ${colors.darkBlue} 0%, ${colors.mediumBlue} 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1rem;
          font-family: Montserrat, sans-serif;
          z-index: 9999;
        }

        .verification-card {
          background-color: ${colors.white};
          border-radius: 15px;
          border: 1px solid ${colors.mediumBlue};
          padding: 0;
          width: 100%;
          max-width: 400px;
          box-shadow:
            0 50px 100px rgba(0, 0, 0, 0.4),
            0 25px 50px rgba(0, 0, 0, 0.25),
            0 12px 24px rgba(0, 0, 0, 0.15),
            0 6px 12px rgba(0, 0, 0, 0.1),
            0 2px 4px rgba(0, 0, 0, 0.05);
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          transform: translateY(-8px);
        }

        .verification-card:hover {
          transform: translateY(-16px);
          box-shadow:
            0 80px 160px rgba(0, 0, 0, 0.5),
            0 40px 80px rgba(0, 0, 0, 0.35),
            0 20px 40px rgba(0, 0, 0, 0.25),
            0 10px 20px rgba(0, 0, 0, 0.15),
            0 4px 8px rgba(0, 0, 0, 0.1);
        }

        .logo-header {
          background-image: linear-gradient(rgba(0, 0, 0, 0.4), rgba(0, 0, 0, 0.4)), url(${BandCrowdBg});
          background-size: cover;
          background-position: center;
          padding: 1rem;
          display: flex;
          justify-content: center;
          align-items: center;
          border-radius: 15px 15px 0 0;
          position: relative;
          top: 0;
          left: 0;
          right: 0;
          margin: 0;
        }

        .content-area {
          padding: 1.5rem;
        }

        /* Button styles with 15px border radius and shadow system */
        .btn {
          padding: 0.65rem 1rem;
          border: 1px solid #e5e7eb;
          border-radius: 15px;
          font-size: 0.9rem;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          outline: none;
          position: relative;
          overflow: hidden;
        }

        .btn:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }

        .btn-primary {
          background-color: ${colors.brightBlue};
          color: ${colors.white};
          width: 100%;
          border: 1px solid ${colors.brightBlue};
          box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1), 0 15px 35px rgba(0, 0, 0, 0.15);
        }

        .btn-primary:hover:not(:disabled) {
          background-color: #0090d8;
          transform: translateY(-4px);
          box-shadow: 0 12px 25px rgba(0, 0, 0, 0.15), 0 25px 50px rgba(0, 0, 0, 0.2);
        }

        .btn-primary:active:not(:disabled) {
          transform: translateY(-2px) scale(0.98);
        }

        .btn-secondary {
          flex: 1;
          background-color: transparent;
          color: ${colors.brightBlue};
          border: 2px solid ${colors.brightBlue};
          font-size: 0.85rem;
          margin-top: 0.5rem;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(1, 162, 241, 0.15);
        }

        .btn-secondary:hover:not(:disabled) {
          background-color: ${colors.brightBlue};
          color: ${colors.white};
          transform: translateY(-3px);
          box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15), 0 16px 35px rgba(1, 162, 241, 0.25);
        }

        .btn-secondary:active:not(:disabled) {
          transform: translateY(-1px) scale(0.98);
        }

        /* Icon container */
        .icon-container {
          font-size: 2.5rem;
          margin-bottom: 0.75rem;
          animation: pulse 2s ease-in-out infinite;
        }

        /* Info box styling */
        .info-box {
          background-color: rgba(0, 208, 132, 0.05);
          border-radius: 15px;
          padding: 0.75rem;
          margin-bottom: 1rem;
          border: 1px solid #e5e7eb;
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1), 0 8px 20px rgba(0, 0, 0, 0.12);
        }

        /* Animations */
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(0.95); }
        }

        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Mobile responsiveness */
        @media (max-width: 480px) {
          .verification-card {
            max-width: 95%;
          }

          .content-area {
            padding: 1.25rem;
          }
        }
      `}</style>

      <div className="email-verified-container">
        <div className="verification-card">
          {/* Logo Header */}
          <div className="logo-header">
            <img
              src={GigifyLogo}
              alt="GIGiFY"
              style={{ height: "40px", width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)" }}
            />
          </div>

          {/* Content Area */}
          <div className="content-area">
            <div style={{ textAlign: "center" }}>
              <div className="icon-container">
                ✅
              </div>

              <h2 style={{ color: colors.mediumBlue, fontSize: "1.5rem", marginBottom: "0.75rem", fontWeight: "600", margin: "0 0 0.75rem 0" }}>
                Email Verified!
              </h2>

              <div className="info-box">
                <p style={{ color: colors.mediumBlue, fontSize: "0.9rem", lineHeight: "1.4", margin: "0 0 0.5rem 0" }}>
                  Your email verification is complete! Your GIGiFY account is now active and ready to use.
                </p>
              </div>

              <div className="info-box">
                <p style={{ margin: "0 0 0.5rem 0", fontWeight: "600", color: colors.mediumBlue, fontSize: "0.85rem" }}>
                  📌 Next Steps:
                </p>
                <ul style={{
                  margin: 0,
                  paddingLeft: "1.5rem",
                  textAlign: "left",
                  fontSize: "0.8rem",
                  color: colors.mediumGray,
                  lineHeight: "1.4"
                }}>
                  <li>Return to your original browser tab to continue</li>
                  <li>If that tab was closed, click "Go to Dashboard" below</li>
                  <li>Complete your profile setup to start using GIGiFY</li>
                </ul>
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <button
                  className="btn btn-primary"
                  onClick={handleLoginRedirect}
                >
                  {auth.currentUser ? 'Go to Dashboard' : 'Login Page'}
                </button>

                <button
                  className="btn btn-secondary"
                  onClick={handleCloseNow}
                >
                  Close Tab
                </button>
              </div>

              {countdown > 0 && (
                <p style={{ marginTop: "0.75rem", fontSize: "0.75rem", color: colors.darkGray, fontStyle: "italic" }}>
                  This tab will automatically close in {countdown} seconds
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmailVerified;